// The cart money the surfaces show, built on `cartValue` (the lines
// summed at the prices the server locked on them, `cartTotals.ts`).
//
// `lineValue` is one line's value: what the side panel row and the open-cart PRICE column
// show. `settledCartValue` is the live cart value while a write may be in flight: a SKU the
// Shopper has just added has no locked price until the server answers, so it is left out
// of the sum while its own write is pending, and the value keeps showing what the priced
// lines are worth instead of flickering to UNKNOWN_TOTAL on every add. Once the server
// answers, the line carries its price and counts. An unpriced line with no write in flight
// still makes the value unknown, and so does a cart whose every line is awaiting its price
// (the first add to an empty cart): no figure is claimed for lines none of which is priced.
// `totalOfCartValues` is the value across several carts (the choice screen's open-carts
// summary): unknown when any cart's value is. `valueShareLabel` is an Order Review department
// band's "N% of value": the band's value as a share of the order total (the depot cart's
// "Total excl. VAT"), as SL1.0's department "Percentage of current Order". It is computed from
// the same locked prices, so while the cart lines carry the provisional price (Story 11.14 not
// yet landed) the share is one of provisional values too.
import type { PriceKind } from '../api/price-quotes.client';
import type { MoneyValue } from '../shared/money';
import { cartDeposit, cartSaving, cartValue, toCents } from './cartTotals';

/** The money facts of a cart line, as the basket and review wires carry them. The deposit
 *  (ADO-2246: the locked rate per case and its line value) is never part of the goods value:
 *  `cartValue` reads the prices only, `cartDeposit` the deposit only. */
export interface PricedLine {
  readonly quantity: number;
  readonly unitPrice?: MoneyValue;
  readonly lineTotal?: MoneyValue;
  readonly depositPerUnit?: MoneyValue;
  readonly lineDeposit?: MoneyValue;
  /** ADO-2249 (DEC-6): the line's price type and per-case was price, which its saving is worked
   *  out from (`lineSaving`, `cartSaving`); never part of the goods value. */
  readonly priceKind?: PriceKind;
  readonly wasPrice?: MoneyValue;
}

/** One line's value (its locked line total, else unit price times quantity), or `null`
 *  when the line carries no locked price yet. */
export function lineValue(line: PricedLine): MoneyValue | null {
  return cartValue([line]);
}

/** The lines a cart total counts, leaving out a line that has no price only because its own
 *  write is still in flight (`pending`); `null` when that leaves nothing of a cart that has
 *  lines (no figure is claimed). One rule for the goods total and the deposit total (ADO-2246,
 *  AC5), so the two always count the same lines. */
function settledLines<Line extends PricedLine & { readonly skuCode: string }>(
  lines: readonly Line[],
  pending: ReadonlySet<string>,
): readonly Line[] | null {
  const counted = lines.filter(
    (line) =>
      line.unitPrice !== undefined || line.lineTotal !== undefined || !pending.has(line.skuCode),
  );
  return counted.length === 0 && lines.length > 0 ? null : counted;
}

/** The cart value, leaving out a line that has no price only because its own write is
 *  still in flight (`pending`). */
export function settledCartValue(
  lines: readonly (PricedLine & { readonly skuCode: string })[],
  pending: ReadonlySet<string>,
): MoneyValue | null {
  const counted = settledLines(lines, pending);
  return counted === null ? null : cartValue(counted);
}

/** ADO-2246: the cart's deposit total over exactly the lines `settledCartValue` counts. Unknown
 *  when any counted line's deposit is, or when the goods total would be unknown for want of any
 *  priced line. */
export function settledCartDeposit(
  lines: readonly (PricedLine & { readonly skuCode: string })[],
  pending: ReadonlySet<string>,
): MoneyValue | null {
  const counted = settledLines(lines, pending);
  return counted === null ? null : cartDeposit(counted);
}

/** Pricing migration 18 (ADO-2249, review cycle 7): the cart's total saving over exactly the
 *  lines `settledCartValue` counts, so an add still waiting for the server holds the figure as
 *  the goods and deposit totals do. Unknown (`null`, the dash) when any counted line's saving is,
 *  or when every line is still waiting. */
export function settledCartSaving(
  lines: readonly (PricedLine & { readonly skuCode: string })[],
  pending: ReadonlySet<string>,
): MoneyValue | null {
  const counted = settledLines(lines, pending);
  return counted === null ? null : cartSaving(counted);
}

/** The sum of several carts' values, in integer cents (through `cartValue`, each value as one
 *  line total). `null` when any is unknown, when their currencies differ, or when there are no
 *  carts: no figure is claimed for nothing. */
export function totalOfCartValues(values: readonly (MoneyValue | null)[]): MoneyValue | null {
  if (values.length === 0) return null;
  const lines: PricedLine[] = [];
  for (const value of values) {
    if (value === null) return null;
    lines.push({ quantity: 1, lineTotal: value });
  }
  return cartValue(lines);
}

/**
 * `part` as a share of `whole`, in whole percent: "11% of value". As SL1.0 computes a
 * department's "Percentage of current Order" (`get_orders_cost.php:578`,
 * `$totalVal > 0 ? number_format(dept / total * 100) : 0`): plain rounding, and 0 when the
 * whole is not above zero. `null` (no figure claimed) when either value is unknown or their
 * currencies differ, a state SL1.0 does not have.
 */
export function valueShareLabel(part: MoneyValue | null, whole: MoneyValue | null): string | null {
  if (part === null || whole === null || part.currency !== whole.currency) return null;
  const partCents = toCents(part.amount);
  const wholeCents = toCents(whole.amount);
  if (!Number.isFinite(partCents) || !Number.isFinite(wholeCents)) return null;
  const percent = wholeCents > 0 ? Math.round((partCents * 100) / wholeCents) : 0;
  return `${percent}% of value`;
}
